/**
 * <LiveStage> — la scène d'un live, côté élève (organism).
 *
 * Ce que l'élève voit en visio (`RoomView` aujourd'hui) : le tuteur en grand — ou l'écran qu'il
 * partage. Ma caméra (« Toi ») se pose en vignette dans le coin bas droit, dans tous les cas : je me
 * vois toujours.
 *
 * **Le partage d'écran** (Wajih, 20/10) : l'image partagée ne porte plus la caméra du masteur dans son
 * coin. Elle s'en retire, avec Sam et ma vignette, dans une rangée sous elle ou à sa droite
 * (`stageLayout`), et rien ne mord plus sur ce qu'il montre. Au grand écran, un bouton de la rangée
 * l'agrandit à tout l'écran, l'énoncé masqué (`shareFullScreen`), comme l'ancien live qui passait la
 * visio en grand pendant un partage ; la page la remet à sa place quand le partage s'arrête.
 *
 * Les autres élèves d'un cours partagé ne se montrent pas sur la scène (Wajih, 20/10 : ni tuile, ni
 * caméra, ni visage) : l'en-tête dit combien d'élèves sont connectés (`<LiveSessionBar>`).
 *
 * **Mes autres appareils** (Wajih, daily du 21/10) : connecté ailleurs aussi (un navigateur resté
 * ouvert), je le vois sur une bande collée en haut de ma vignette (`<LiveTwinStrip>`, `twins`) : ma
 * vignette reste l'appareil où je suis, la bande se partage entre les autres, chacun avec ce qu'il
 * envoie (l'écran qu'il partage, sinon sa caméra).
 *
 * **Retourner ma caméra** (Wajih, 21/10 : une fonction perdue, à remettre) : ma vignette touchée fait
 * paraître une petite icône au milieu d'elle, qui retourne ma caméra (`onFlipCamera`) ; touchée encore,
 * elle la range. L'écran ne la donne que si une autre caméra est là et que la mienne filme : sinon ma
 * vignette ne se touche pas, rien ne l'encombre.
 *
 * **Le Concentré se voit sur la visio** (Wajih, 20/10) : c'est surtout elle qu'il change. En Concentré,
 * la vidéo du masteur porte l'étiquette du mode (`<SoundModeBadge>`, « Concentré » et le son coupé, au
 * violet du mode) dans son coin haut gauche, et la suit dans l'orbite du tandem. En Dialogue, rien.
 *
 * **Le tandem dans la visio** (`tandem`, Wajih 19/10) : quand la scène est à l'écran, Sam et le masteur
 * s'y tiennent, sans bulle en plus — jamais le masteur deux fois sur le même écran. En Concentré (et
 * pendant l'attente du masteur), Sam passe devant, en grand, en bas à gauche ; le masteur passe derrière
 * en visage rond, comme dans la bulle tandem (Wajih, 20/10), légèrement superposé à Sam comme la miniature
 * (Wajih, 22/10 : la même taille, le même décalage, le même recouvrement, à l'échelle), en retrait en
 * haut à droite, sous un voile sombre léger : sa vidéo recule dans l'orbite et s'y fond dans ce visage
 * rond (`stageLayout.tutorRound`). Ce que dit Sam sort juste au-dessus de lui (Wajih, 22/10 : posé
 * au-dessus des deux, on l'attribuait au masteur). En Dialogue, la vidéo reprend toute la scène et Sam
 * passe en petit, opaque, à côté du masteur : au-dessus de son prénom, ou de sa caméra pendant un
 * partage (Wajih, 20/10). La bascule est une orbite (Wajih, 20/10, `useTandemOrbit`) : la vidéo et
 * Sam tournent autour de leur centre commun, celui qui avance grandit et perd son voile, celui qui
 * recule rapetisse et le prend ; à la durée `entree` et à la courbe de la marque, l'arrivée à plat ; en
 * mouvement réduit, chacun est aussitôt à sa place. Sur Android, la vidéo (une `SurfaceView`) ne suit
 * pas l'orbite : la photo du masteur la remplace le temps du mouvement. Sam,
 * touché, ouvre sa conversation, devant comme en petit (Wajih, 20/10 : en petit, il n'était pas
 * cliquable). Sans Sam (une salle sans checkpoint), le masteur garde la scène.
 *
 * **Plusieurs tuteurs** (Wajih, 20/10) : la scène et le tandem ne montrent que le tuteur que l'élève
 * attend (l'écran le désigne) ; les autres tuteurs connectés, et une autre caméra de ce tuteur quand
 * il filme depuis deux appareils, sont de petits ronds (`tutorRounds`), sans tandem ni place en grand.
 *
 * La place de chaque chose se décide dans `stageLayout` : rien ne se chevauche, quel que soit l'état
 * des micros et des caméras.
 *
 * Le tuteur attendu et pas encore là : son visage estompé tient sa place. La connexion qui
 * s'établit ou se rétablit couvre la scène d'un voile, avec ses mots d'aujourd'hui et la seule
 * sortie qu'elle offre (« Annuler », ou « Réessayer » quand la reconnexion piétine) ; pas de roue :
 * les mots de l'attente, soulignés (`<WaitingText>`).
 *
 * Contrôlée : participants, partage et connexion arrivent en props. Les vraies pistes aussi, par une
 * fente de rendu (`renderVideo`) : la scène demande celle du tuteur, la mienne et l'écran partagé,
 * l'écran les rend (LiveKit), et chaque tuile pose la sienne à la place de la photo ; sans fente, la
 * scène reste la maquette. La page lui retire le partage d'écran en Concentré, comme aujourd'hui.
 */
import React, { useEffect, useMemo, useState } from "react"
import { Animated, Platform, Pressable, View, type LayoutChangeEvent } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { useFadeIn } from "../../hooks/useFadeIn"
import { useSettled } from "../../hooks/useSettled"
import { ORBIT_STAGE_FORWARD_MS, tutorFade } from "../../hooks/tandemOrbit"
import { useTandemOrbit } from "../../hooks/useTandemOrbit"
import { SamPeek } from "../../molecules/SamPeek"
import { SamTile } from "../../molecules/SamTile"
import { LiveTwinStrip } from "../../molecules/LiveTwinStrip"
import { SoundModeBadge } from "../../molecules/SoundModeBadge"
import { type UILiveParticipant, VideoTile } from "../../molecules/VideoTile"
import { samPeekText } from "../../utils/samPeek"
import { slopTo } from "../../utils/touchTarget"
import { type UITandemLead } from "../LiveTandemBubble"
import {
    type UILiveConnection,
    type UILiveToggle,
    type UILiveTwin,
    type UILiveVideoFit,
    type UILiveVideoSource,
} from "../../../ports/primary/ui/types/studentLive"
import {
    type BottomPlace,
    fromBottom,
    MARK_INSET,
    type Rect,
    ROUND,
    square,
    STAGE_BUTTON,
    stageLayout,
    STAGE_INSET,
    THUMB,
    TUTOR_BACK_VEIL,
} from "./stageLayout"

// La connexion au live : sa forme vit dans `ports/primary/ui/types` (socle du live).
export type { UILiveConnection, UILiveVideoFit, UILiveVideoSource }

/** Le tandem dans la visio : qui est devant, et Sam. */
export type UIStageTandem = {
    /** Sam devant en Concentré et pendant l'attente, le masteur en Dialogue. */
    lead: UITandemLead
    /** Sam. Absent : le masteur garde la scène. */
    sam?: {
        onOpen: () => void
        /** Ce que Sam vient de dire, au-dessus de lui, quand il est devant. */
        peek?: string | null
        onDismissPeek?: () => void
    }
}

export type LiveStageProps = {
    tutor: UILiveParticipant
    /** Le tuteur n'est pas encore entré : son visage estompé. Défaut `false`. */
    tutorAbsent?: boolean
    /**
     * Les petits ronds : les autres tuteurs connectés (en observation, d'un autre live de la salle), et
     * une autre caméra du tuteur quand il filme depuis deux appareils. Ni tandem ni place en grand ; leur
     * piste par `renderVideo("round:<id>")`, leur photo sans elle. Défaut : aucun.
     */
    tutorRounds?: UILiveParticipant[]
    me: UILiveParticipant
    /**
     * Mes autres appareils connectés au live : la bande collée en haut de ma vignette, leur piste par
     * `renderVideo("twin:<id>")`. Défaut : aucun, pas de bande.
     */
    twins?: UILiveTwin[]
    /**
     * Retourner ma caméra : offert seulement quand une autre caméra est là. Ma vignette touchée fait
     * paraître l'icône qui l'appelle. Absent (défaut) : ma vignette ne se touche pas.
     */
    onFlipCamera?: () => void
    /** Le masteur a posé le Concentré : l'étiquette du mode sur sa vidéo. Défaut `false`. */
    focused?: boolean
    /** L'écran que le tuteur partage : il prend la scène, sa caméra passe en vignette. */
    screenUri?: string
    /**
     * La fente des vraies pistes : la scène demande `tutor`, `me` et `screen`, l'écran rend la piste
     * (`VideoView` de LiveKit) ou `null` quand il n'y en a pas. Une piste `screen` rendue vaut un
     * partage d'écran, comme `screenUri`. `fit` : comment la piste remplit sa place (la caméra du masteur
     * en grand se pose entière, `contain`) ; sans lui, comme la source le veut. Absente : la maquette
     * (photos et image).
     */
    renderVideo?: (source: UILiveVideoSource, fit?: UILiveVideoFit) => React.ReactNode
    /** La connexion en cours. `null` ou absent : connecté. */
    connection?: UILiveConnection | null
    /** « Annuler » pendant la connexion : l'élève ressort du live. */
    onCancelConnection?: () => void
    /** « Réessayer » quand la reconnexion piétine. Absent : « Annuler ». */
    onRetryConnection?: () => void
    /** Le tandem Sam–masteur dans la visio. Absent (défaut) : la scène sans Sam. */
    tandem?: UIStageTandem
    /**
     * « Revenir à l'activité », en haut à droite : la visio rouverte par l'élève au grand écran en
     * Concentré se referme. Absent (défaut) : pas de bouton.
     */
    onHide?: () => void
    /**
     * Le partage en grand, l'énoncé masqué : son état et son bouton, dans la rangée des visages, pendant
     * un partage. Absent (défaut, et au téléphone, où la visio est déjà seule) : pas de bouton.
     */
    shareFullScreen?: UILiveToggle
    testID?: string
}

/** Le tuteur partage son écran, à l'écran : une image, ou une vraie piste rendue. */
export function stageIsSharing(
    stage: Pick<LiveStageProps, "screenUri" | "renderVideo" | "tutorAbsent">,
    screenVideo: React.ReactNode = stage.renderVideo?.("screen") ?? undefined
): boolean {
    return (stage.screenUri != null || screenVideo != null) && stage.tutorAbsent !== true
}

const TEXTS = {
    connecting: "Connexion…",
    reconnecting: "Reconnexion…",
    unreachable: "Le live n'est plus joignable",
    cancel: "Annuler",
    retry: "Réessayer",
    hide: "Revenir à l'activité",
    shareFull: "Agrandir l'écran partagé",
    shareBack: "Revenir à l'énoncé",
    me: "Toi",
    showFlip: "Montre le bouton pour retourner la caméra",
    flip: "Retourner la caméra",
}

const NO_ROUNDS: UILiveParticipant[] = []
const NO_TWINS: UILiveTwin[] = []

/** L'écart de l'orbite du tandem à mi-course, en part de la largeur de la scène. */
const STAGE_SWING = 0.08
/** Les boutons de la scène font `STAGE_BUTTON` (36) : leur zone de toucher s'étend à 44. */
const STAGE_HIT_SLOP = slopTo(STAGE_BUTTON)

/** Le temps qu'une mesure de la scène se tienne avant que l'orbite s'y règle : une barre a fini de s'ouvrir. */
const ORBIT_SETTLE_MS = 150

/** La place d'un rond de la règle : le visage du masteur en retrait, Sam devant. */
const roundRect = (round: { center: { x: number; y: number }; size: number }): Rect => square(round.center, round.size)

/** La scène mesurée : le tandem s'y place. Avant la mesure, rien ne bouge. */
type Size = { width: number; height: number }

export function LiveStage(props: LiveStageProps) {
    const {
        tutor,
        tutorAbsent = false,
        tutorRounds = NO_ROUNDS,
        me,
        twins = NO_TWINS,
        onFlipCamera,
        focused = false,
        screenUri,
        renderVideo,
        connection = null,
        onCancelConnection,
        onRetryConnection,
        tandem,
        onHide,
        shareFullScreen,
        testID,
    } = props
    // En grand, la caméra du masteur se pose entière, le fond de la tuile autour (Wajih, daily du 21/10 :
    // rognée, au téléphone, on n'en voyait qu'une partie) ; son visage rond et sa vignette la rognent.
    const tutorVideo = renderVideo?.("tutor") ?? undefined
    const tutorFullVideo = renderVideo?.("tutor", "contain") ?? undefined
    const meVideo = renderVideo?.("me") ?? undefined
    const screenVideo = renderVideo?.("screen") ?? undefined
    const sharing = stageIsSharing({ screenUri, tutorAbsent }, screenVideo)

    // État d'interface : l'icône qui retourne ma caméra, montrée par ma vignette touchée. Plus rien à
    // retourner (une seule caméra, la mienne coupée) : elle se range.
    const canFlip = onFlipCamera != null && me.cameraOff !== true
    const [flipShown, setFlipShown] = useState(false)
    useEffect(() => {
        if (!canFlip) setFlipShown(false)
    }, [canFlip])

    // État d'interface : la taille de la scène, où le tandem se place.
    const [size, setSize] = useState<Size | null>(null)
    const onLayout = (event: LayoutChangeEvent) => {
        const { width, height } = event.nativeEvent.layout
        setSize((current) => (current?.width === width && current.height === height ? current : { width, height }))
    }

    const sam = tandem?.sam
    // Sans Sam, le masteur garde la scène.
    const samLeads = sam != null && tandem?.lead === "sam"

    const layoutFor = (box: Size | null) =>
        box != null ? stageLayout(box.width, box.height, sharing, tutorRounds.length, twins.length > 0) : null
    const layout = layoutFor(size)
    const share = layout?.share ?? null

    // L'orbite se règle sur la scène posée (`useSettled`) : une barre qui s'ouvre au-dessus d'elle la remesure
    // à chaque image, et refaire ses interpolations à chaque fois les rattacherait au pilote natif à chaque
    // image. Au repos, ce qui se voit de l'orbite ne dépend pas de la hauteur : le duo est posé depuis le bas,
    // et la vidéo du masteur derrière Sam, effacée, ne se voit pas.
    const orbitSize = useSettled(size, ORBIT_SETTLE_MS)
    const orbitLayout = orbitSize === size ? layout : layoutFor(orbitSize)
    const orbitShare = orbitLayout?.share ?? null

    // L'orbite du tandem (Wajih, 20/10) : la vidéo du masteur, pleine devant (ou à la place de son
    // partage), réduite derrière Sam ; Sam, en grand devant, en petit à côté du masteur. Une échelle se
    // prend au centre : chacun est posé à sa place de devant, et l'orbite le déplace puis le réduit.
    const orbit = useTandemOrbit({
        samLeads,
        // Vers Sam devant, la vidéo du masteur se réduit à vue vers son visage (`ORBIT_STAGE_FORWARD_MS`).
        forwardDuration: ORBIT_STAGE_FORWARD_MS,
        geometry:
            orbitLayout != null && orbitSize != null && sam != null
                ? {
                      sam: {
                          front: { center: orbitLayout.samFront.center, scale: 1 },
                          back: orbitLayout.samSmall,
                      },
                      other: {
                          front: {
                              center:
                                  orbitShare != null
                                      ? {
                                            x: orbitShare.screen.x + orbitShare.screen.width / 2,
                                            y: orbitShare.screen.y + orbitShare.screen.height / 2,
                                        }
                                      : { x: orbitSize.width / 2, y: orbitSize.height / 2 },
                              scale: 1,
                          },
                          back: orbitLayout.tutorBack,
                      },
                      // Comme la bulle : celui qui recule passe à droite, vers le retrait, celui qui avance à
                      // gauche, vers le devant ; la vidéo, aux trois quarts de sa taille à mi-course, reste dans
                      // la scène.
                      swing: { x: STAGE_SWING * orbitSize.width, y: 0 },
                  }
                : null,
    })
    // Le voile, le fondu de la vidéo dans le visage rond, et celui du visage rond, gardés d'un rendu à
    // l'autre : une interpolation neuve à chaque rendu se rattache au graphe natif à chaque
    // `ActiveSpeakersChanged` (RN 0.81).
    // Vers Sam devant, la vidéo se voit se réduire jusqu'à son visage et ne s'y fond qu'à la fin de la course ;
    // au retour en Dialogue, le fondu se fait à mi-course, comme avant (Wajih, 24/10). Lu sur la phase, il ne
    // saute pas quand un relais rebrousse chemin (`tutorFade`).
    const backVeil = orbit.other?.back
    const phase = orbit.phase
    const recede = useMemo(() => {
        if (backVeil == null) return null
        const fade = tutorFade()
        return {
            veil: backVeil.interpolate({ inputRange: [0, 1], outputRange: [0, TUTOR_BACK_VEIL] }),
            video: phase.interpolate({ inputRange: fade.inputRange, outputRange: fade.video }),
            round: phase.interpolate({ inputRange: fade.inputRange, outputRange: fade.round }),
        }
    }, [backVeil, phase])
    const tutorPlace = orbit.other != null ? { transform: orbit.other.transform, opacity: recede?.video } : null
    // Sam se rend à sa taille de devant, et se réduit pour passer en petit : l'image reste nette. Posé
    // depuis le bas, comme le masteur en retrait et son étiquette : la scène qui perd du haut (une barre
    // qui s'ouvre au-dessus d'elle) ne les déplace pas, même une image avant d'être remesurée (daily du
    // 23/10 : Sam sautait de la hauteur de la barre, puis revenait).
    const height = size?.height ?? 0
    const samPlace =
        layout != null && orbit.sam != null
            ? {
                  ...fromBottom(roundRect(layout.samFront), height),
                  transform: orbit.sam.transform,
              }
            : null
    // Sur Android, la vidéo ne suit pas l'orbite : sa photo le temps du mouvement. Derrière Sam, elle est
    // dans le visage rond seulement : une `SurfaceView` ne suit pas l'opacité, la grande ne la porte plus.
    // Ailleurs, la grande garde sa vidéo pendant qu'elle rétrécit et se fond dans le visage rond (Wajih,
    // daily du 21/10 : sans elle, son initiale remplissait la scène avant que l'orbite parte).
    const tutorStageVideo =
        orbit.showVideo && !(samLeads && orbit.other != null && Platform.OS === "android") ? tutorFullVideo : undefined
    const round = layout?.tutorRound ?? null
    // Ce que dit Sam sort de lui une fois l'orbite posée, en fondu (daily du 23/10 : posée au départ de
    // l'orbite, sa bulle arrivait avant lui, pendant que la visio rétrécissait).
    const peekText = samLeads && orbit.landed ? samPeekText(sam?.peek) : ""
    const peekIn = useFadeIn(peekText !== "")

    return (
        <View style={styles.root} testID={testID}>
            <View style={styles.main} onLayout={onLayout}>
                {/* Pendant un partage, l'image se pose à sa place, hors de la rangée des visages. */}
                <Animated.View
                    style={[share != null ? styles.place(share.screen) : styles.fill, tutorPlace]}
                    pointerEvents="box-none"
                >
                    <VideoTile
                        participant={tutor}
                        size="stage"
                        screenUri={sharing ? screenUri : undefined}
                        screenVideo={sharing && orbit.showVideo ? screenVideo : undefined}
                        video={tutorStageVideo}
                        absent={tutorAbsent}
                    />
                    {recede != null && (
                        // Derrière Sam, la vidéo passe sous un voile sombre : elle recule sans se délaver.
                        <Animated.View style={[styles.backVeil, { opacity: recede.veil }]} pointerEvents="none" />
                    )}
                </Animated.View>

                {round != null && recede != null && (
                    // Le masteur derrière Sam : un visage rond, comme dans la bulle tandem.
                    <Animated.View
                        style={[styles.anchored(fromBottom(roundRect(round), height)), { opacity: recede.round }]}
                        pointerEvents="none"
                    >
                        <VideoTile
                            participant={tutor}
                            size="round"
                            diameter={round.size}
                            video={samLeads && orbit.showVideo ? tutorVideo : undefined}
                            absent={tutorAbsent}
                        />
                        <Animated.View
                            style={[styles.roundVeil(round.size), { opacity: recede.veil }]}
                            pointerEvents="none"
                        />
                    </Animated.View>
                )}

                {focused &&
                    (layout != null && recede != null ? (
                        // L'étiquette du Concentré, au coin bas droit du visage rond du masteur.
                        <Animated.View
                            style={[styles.roundMark(fromBottom(layout.tutorMark, height)), { opacity: recede.round }]}
                            pointerEvents="none"
                        >
                            <SoundModeBadge mode="focused" />
                        </Animated.View>
                    ) : (
                        // Sans Sam, le masteur garde la scène : l'étiquette dans le coin haut gauche de sa vidéo.
                        <View style={styles.focusedMark} pointerEvents="none">
                            <SoundModeBadge mode="focused" />
                        </View>
                    ))}
                {sharing && (
                    <View style={share != null ? styles.place(share.tutorCam) : styles.corner("top", "right")}>
                        <VideoTile participant={tutor} size="thumb" video={tutorVideo} />
                    </View>
                )}
                {share != null && shareFullScreen != null && (
                    <View style={styles.place(share.fullScreen)}>
                        <IconButton
                            iconName={shareFullScreen.on ? "compress-alt" : "expand-alt"}
                            size={STAGE_BUTTON}
                            iconSize={16}
                            color="backdrop"
                            iconColor="white"
                            hitSlop={STAGE_HIT_SLOP}
                            onPress={shareFullScreen.onToggle}
                            accessibilityLabel={shareFullScreen.on ? TEXTS.shareBack : TEXTS.shareFull}
                        />
                    </View>
                )}

                {sam != null && layout != null && samPlace != null && (
                    <Animated.View style={[styles.sam, samPlace]}>
                        <SamTile onPress={sam.onOpen} size={layout.samFront.size} unread={peekText !== ""} />
                    </Animated.View>
                )}
                {peekText !== "" && sam != null && layout != null && (
                    // Juste au-dessus de Sam, sur son bord gauche, sa queue vers lui : la bulle sort de celui qui
                    // est devant, loin du masteur en retrait.
                    <Animated.View
                        style={[
                            styles.peek(height - layout.peek.y, layout.peek.x, layout.peek.width),
                            { opacity: peekIn },
                        ]}
                        pointerEvents="box-none"
                    >
                        <SamPeek text={peekText} onPress={sam.onOpen} onDismiss={sam.onDismissPeek} tail="left" />
                    </Animated.View>
                )}

                {onHide != null && !sharing && (
                    // En haut à droite : la vidéo du masteur y est réduite, son micro coupé ne s'y pose pas.
                    <View style={styles.corner("top", "right")}>
                        <IconButton
                            iconName="times"
                            size={STAGE_BUTTON}
                            iconSize={16}
                            color="backdrop"
                            iconColor="white"
                            hitSlop={STAGE_HIT_SLOP}
                            onPress={onHide}
                            accessibilityLabel={TEXTS.hide}
                        />
                    </View>
                )}

                {/* Les autres tuteurs, en petits ronds, là où la règle les pose (`stageLayout`). */}
                {layout?.rounds.map((rect, index) => {
                    const round = tutorRounds[index]
                    return (
                        <View key={round.id} style={styles.place(rect)}>
                            <VideoTile
                                participant={round}
                                size="round"
                                diameter={ROUND}
                                video={renderVideo?.(`round:${round.id}`) ?? undefined}
                            />
                        </View>
                    )
                })}

                {layout?.twins != null && (
                    // Mes autres appareils, collés en haut de ma vignette. Chacun se montre entier (Wajih, daily
                    // du 23/10 : rognée, sa caméra coupait le visage).
                    <View style={styles.anchored(fromBottom(layout.twins, height))}>
                        <LiveTwinStrip
                            twins={twins}
                            me={me}
                            width={layout.twins.width}
                            height={layout.twins.height}
                            renderVideo={renderVideo != null ? (id) => renderVideo(`twin:${id}`, "contain") : undefined}
                        />
                    </View>
                )}

                {/* Ma vignette : en bas à droite, toujours, au-dessus de tout. */}
                <View style={styles.corner("bottom", "right")}>
                    {canFlip ? (
                        <Pressable
                            onPress={() => setFlipShown((shown) => !shown)}
                            accessibilityRole="button"
                            accessibilityLabel={TEXTS.me}
                            accessibilityHint={TEXTS.showFlip}
                        >
                            <VideoTile participant={me} size="thumb" me video={meVideo} />
                        </Pressable>
                    ) : (
                        <VideoTile participant={me} size="thumb" me video={meVideo} />
                    )}
                    {canFlip && flipShown && (
                        <View style={styles.flip} pointerEvents="box-none">
                            <IconButton
                                iconName="camera-rotate"
                                size={STAGE_BUTTON}
                                iconSize={16}
                                color="backdrop"
                                iconColor="white"
                                hitSlop={STAGE_HIT_SLOP}
                                onPress={onFlipCamera}
                                accessibilityLabel={TEXTS.flip}
                            />
                        </View>
                    )}
                </View>

                {connection != null && (
                    <View style={styles.veil}>
                        <Stack gap="3" align="center">
                            {connection === "unreachable" ? (
                                // Rien ne se relance : pas de mots de l'attente.
                                <Text size="text-lg" weight="semibold" color="white" center>
                                    {TEXTS.unreachable}
                                </Text>
                            ) : (
                                <WaitingText size="text-lg" weight="semibold" color="white">
                                    {connection === "connecting" ? TEXTS.connecting : TEXTS.reconnecting}
                                </WaitingText>
                            )}
                            {onRetryConnection != null ? (
                                <TextButton label={TEXTS.retry} color="white" onPress={onRetryConnection} />
                            ) : (
                                onCancelConnection != null && (
                                    <TextButton label={TEXTS.cancel} color="white" onPress={onCancelConnection} />
                                )
                            )}
                        </Stack>
                    </View>
                )}
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: {
        flex: 1,
        minHeight: 0,
        padding: STAGE_INSET,
    },
    main: { flex: 1, minHeight: 0 },
    fill: { position: "absolute", top: 0, right: 0, bottom: 0, left: 0 },
    corner: (vertical: "top" | "bottom", horizontal: "left" | "right") => ({
        position: "absolute" as const,
        [vertical]: STAGE_INSET,
        [horizontal]: STAGE_INSET,
    }),
    /** Une place de la règle (`stageLayout`), dans la scène. */
    place: (rect: Rect) => ({
        position: "absolute" as const,
        left: rect.x,
        top: rect.y,
        width: rect.width,
        height: rect.height,
    }),
    sam: { position: "absolute" },
    /** L'icône qui retourne ma caméra, au milieu de ma vignette : loin du nom et des repères de ses coins. */
    flip: {
        position: "absolute",
        left: (THUMB.width - STAGE_BUTTON) / 2,
        top: (THUMB.height - STAGE_BUTTON) / 2,
    },
    /** L'étiquette du Concentré, au coin haut gauche de la vidéo pleine du masteur. */
    focusedMark: { position: "absolute", top: MARK_INSET, left: MARK_INSET },
    /** Une place de la règle posée depuis le bas de la scène (`fromBottom`) : ce qui se tient en bas. */
    anchored: (place: BottomPlace) => ({ position: "absolute" as const, ...place }),
    /** L'étiquette du Concentré, au milieu de sa place (`stageLayout.tutorMark`), au coin bas droit du visage rond. */
    roundMark: (place: BottomPlace) => ({
        position: "absolute" as const,
        ...place,
        flexDirection: "row" as const,
        alignItems: "center" as const,
        justifyContent: "center" as const,
    }),
    /** Le voile du visage rond, à sa forme. */
    roundVeil: (diameter: number) => ({
        position: "absolute" as const,
        top: 0,
        left: 0,
        width: diameter,
        height: diameter,
        borderRadius: diameter / 2,
        backgroundColor: theme.colors["surface-night"],
    }),
    backVeil: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        borderRadius: theme.radius.lg,
        backgroundColor: theme.colors["surface-night"],
    },
    /** Posée au-dessus de Sam (`above` : du bas de sa place au bas de la scène), à partir de son bord gauche. */
    peek: (above: number, left: number, width: number) => ({
        position: "absolute" as const,
        bottom: above,
        left,
        width,
        alignItems: "flex-start" as const,
    }),
    veil: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: theme.radius.lg,
        backgroundColor: theme.colors.backdrop,
    },
}))
